import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

const defaultCampaignProperties = {
    campaignCode: '',
    campaignCodeId: null,
    campaignCodeDescription: null,
    maxFeeCap: null,
    commissionPercent: null
};

type CampaignCodeState = {
    campaignCode: string | null;
    campaignCodeId: number | null;
    campaignCodeDescription: string | null;
    campaignCodeChecked: boolean;
    firstTimeSubscribe: boolean;
    validCampaign: boolean;
    timeoutId: number | null;
    loading: boolean;
    searching: boolean;
    campaignCodeValid: boolean;
    searchingDelayed: boolean;
    validationError: string | null;
    searchingDelayedTimeoutId: number | null;
    isReadOnly: boolean;
    bannerMobileImageUrl: string | null;
    bannerTabletImageUrl: string | null;
    bannerDesktopImageUrl: string | null;
    hasEnded: boolean;
    maxFeeCap?: number | null;
    commissionPercent: string | null;
};

const initialState: CampaignCodeState = {
    ...defaultCampaignProperties,
    campaignCodeChecked: false,
    firstTimeSubscribe: true,
    validCampaign: false,
    timeoutId: null,
    loading: false,
    searching: false,
    campaignCodeValid: false,
    searchingDelayed: false,
    validationError: null,
    searchingDelayedTimeoutId: null,
    isReadOnly: false,
    bannerMobileImageUrl: null,
    bannerTabletImageUrl: null,
    bannerDesktopImageUrl: null,
    hasEnded: false
};

function ensureValidCampaignCodeId<T extends number | string | null>(
    campaignCodeId?: T
): T | null {
    // Cannot publish when campaignCodeId is zero.
    // Fix server side when Riot SYI is used by 100% of members.
    return campaignCodeId || null;
}

function validate(state: CampaignCodeState) {
    if (state.campaignCodeChecked) {
        return {
            ...state,
            validationError: state.campaignCodeValid
                ? null
                : 'syi_campaign_code_invalid'
        };
    }

    return { ...state, validationError: null };
}

const campaignCodeSlice = createSlice({
    name: 'campaignCode',
    initialState,
    reducers: {
        initializeCampaignCodeState: (
            state,
            action: PayloadAction<{
                campaignCode: string | null;
                campaignCodeId: number | undefined;
                campaignCodeDescription?: string;
                isReadOnly?: boolean;
            }>
        ) => {
            return validate({
                ...state,
                ...defaultCampaignProperties,
                campaignCode: action.payload.campaignCode,
                campaignCodeId: ensureValidCampaignCodeId(
                    action.payload.campaignCodeId
                ),
                campaignCodeDescription:
                    action.payload.campaignCodeDescription || null,
                campaignCodeValid:
                    !!action.payload.campaignCodeId ||
                    !action.payload.campaignCode,
                campaignCodeChecked: !!action.payload.campaignCode,
                isReadOnly: !!action.payload.isReadOnly
            });
        },
        setUnverifiedCampaignCode: (
            state,
            action: PayloadAction<string | null>
        ) => {
            return validate({
                ...state,
                ...defaultCampaignProperties,
                campaignCode: action.payload,
                campaignCodeId: null,
                campaignCodeDescription: null,
                campaignCodeValid: !action.payload,
                bannerMobileImageUrl: null,
                bannerTabletImageUrl: null,
                bannerDesktopImageUrl: null
            });
        },
        setCampaignValidationResult: (
            state,
            action: PayloadAction<{
                campaignCode: string;
                campaignCodeId?: number | null;
                campaignCodeDescription?: string | null;
                campaignCodeValid: boolean;
                maxFeeCap?: number;
                bannerMobileImageUrl?: string | null;
                bannerTabletImageUrl?: string | null;
                bannerDesktopImageUrl?: string | null;
                hasEnded?: boolean;
                commissionPercent: string | null;
            }>
        ) => {
            if (state.campaignCode === action.payload.campaignCode) {
                return validate({
                    ...state,
                    ...defaultCampaignProperties,
                    campaignCode: action.payload.campaignCode,
                    campaignCodeId: ensureValidCampaignCodeId(
                        action.payload.campaignCodeId
                    ),
                    campaignCodeDescription:
                        action.payload.campaignCodeDescription || null,
                    campaignCodeValid: action.payload.campaignCodeValid,
                    maxFeeCap: action.payload.maxFeeCap,
                    bannerMobileImageUrl:
                        action.payload.bannerMobileImageUrl ?? null,
                    bannerTabletImageUrl:
                        action.payload.bannerTabletImageUrl ?? null,
                    bannerDesktopImageUrl:
                        action.payload.bannerDesktopImageUrl ?? null,
                    hasEnded: !!action.payload.hasEnded,
                    searching: false,
                    searchingDelayed: false,
                    commissionPercent: action.payload.commissionPercent
                });
            }
        },
        resetFees: (state) => {
            return validate({
                ...state,
                maxFeeCap: initialState.maxFeeCap,
                commissionPercent: initialState.commissionPercent
            });
        },
        resetCampaignBanner: (state) => {
            return validate({
                ...state,
                bannerMobileImageUrl: initialState.bannerMobileImageUrl,
                bannerTabletImageUrl: initialState.bannerTabletImageUrl,
                bannerDesktopImageUrl: initialState.bannerDesktopImageUrl
            });
        },
        toggleCampaignCodeCheckbox: (state, action: PayloadAction<boolean>) => {
            if (action.payload) {
                return validate({
                    ...state,
                    campaignCodeChecked: true,
                    searching: false,
                    searchingDelayed: false
                });
            } else {
                return validate({
                    ...state,
                    campaignCodeChecked: false,
                    campaignCode: initialState.campaignCode,
                    campaignCodeId: initialState.campaignCodeId,
                    campaignCodeDescription: null,
                    searching: false,
                    searchingDelayed: false,
                    hasEnded: false
                });
            }
        },
        validatingCampaignCodeBySearching: (state) => {
            return validate({ ...state, searching: true });
        },
        searchingForCampaignCodeIsDelayed: (state) => {
            return validate({ ...state, searchingDelayed: true });
        }
    }
});

export const {
    initializeCampaignCodeState,
    setUnverifiedCampaignCode,
    setCampaignValidationResult,
    resetFees,
    resetCampaignBanner,
    toggleCampaignCodeCheckbox,
    validatingCampaignCodeBySearching,
    searchingForCampaignCodeIsDelayed
} = campaignCodeSlice.actions;

export default campaignCodeSlice.reducer;
